// frontend/src/components/reports/ReportFilters.tsx
//
// Panel de filtros dirigido por `def.filters`: añadir un filtro es añadir una
// entrada al registro, no tocar este componente.
//
// Las opciones de los `select` se derivan del propio dataset y no de constantes,
// para que no se queden desfasadas cuando cambie el dato de origen.
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppStore } from '@/store'
import { DateRangePicker } from '@/components/common/DateRangePicker'
import { MultiFiltro } from '@/components/common/MultiFiltro'
import {
  BarraFiltrosActivos, descripcionDe, type FiltroActivo,
} from '@/components/common/BarraFiltrosActivos'
import { filterCardCls, filterFieldCls, filterLabelCls } from '@/components/common/FilterBar'
import type { AnyReportRow } from '@/types'
import { distinctPairs, distinctValues } from '@/utils/reportBlocks'
import { etiquetaCatalogoDe } from '@/utils/filtroArticulo'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import type { ReportDef, ReportFilterSpec } from './reportDefs'
import { estadoMulti, valoresMulti, type ReportFiltersState } from './filterPredicates'

interface Props {
  def: ReportDef
  filters: ReportFiltersState
  onChange: (key: string, value: string) => void
  onClear: () => void
  /** Feedback de resultados, ya formateado por la página. */
  resumen?: string
  /** Todas las filas del report, para poblar los desplegables. */
  rows: AnyReportRow[]
  /** Nivel sobre el que se calculan los totales. `null` = el de cabecera. */
  activeLevel: string | null
  /** Niveles elegidos para el pie de totales, en el orden del report. */
  aggLevels: string[]
  onLevels: (tipoFilas: string[]) => void
  /** ¿El pie cuenta sólo las sublíneas que casan con el filtro? */
  soloCoincidencias: boolean
  onSoloCoincidencias: (solo: boolean) => void
}

export function ReportFilters({
  def, filters, onChange, onClear, resumen, rows, activeLevel, aggLevels, onLevels,
  soloCoincidencias, onSoloCoincidencias,
}: Props) {
  const { t } = useTranslation()
  const dark = useAppStore(s => s.dark)
  const fmtArt = useFmtArticulo()

  /** Catálogo de un filtro de códigos: el VALOR sigue siendo el código crudo —es
   *  el que viaja al predicado y a la URL— y el TÍTULO va formateado por rol,
   *  igual que la columna. `MultiFiltro` busca también por `valor`, así que
   *  pegar la referencia cruda sigue encontrando su opción. */
  function catalogoDe(spec: ReportFilterSpec) {
    return distinctPairs(rows, spec.field as keyof AnyReportRow & string, spec.descripcionField)
      .map(o => ({
        valor: o.valor,
        titulo: spec.esArticulo ? fmtArt(o.valor) : o.valor,
        detalle: o.detalle,
      }))
  }

  /**
   * Rótulo de una opción de `select`. Sigue el idioma de la UI cuando el filtro
   * declara prefijo i18n; el VALOR que viaja al predicado no cambia nunca, porque
   * es el de la vista y está en español.
   */
  function opcion(spec: ReportFilterSpec, valor: string) {
    const fallback = spec.optionLabels?.[valor] ?? valor
    return spec.optionLabelPrefix
      ? t(`${spec.optionLabelPrefix}.${valor}`, { defaultValue: fallback })
      : fallback
  }

  /**
   * Cada campo aporta DOS celdas a la rejilla — rótulo y control — en lugar de un
   * contenedor propio. Es lo que hace que todos los rótulos compartan línea y que
   * un control más alto no descuadre la fila.
   */
  function campo(spec: ReportFilterSpec) {
    const label = (
      <label key={`${spec.key}-l`} className={`${filterLabelCls(dark)} self-end`}>
        {t(spec.labelKey)}
      </label>
    )

    if (spec.kind === 'dateWindow') {
      const desde = filters[`${spec.key}Desde`] ?? ''
      const hasta = filters[`${spec.key}Hasta`] ?? ''
      // DateRangePicker ya rotula sus dos extremos («DESDE – HASTA») dentro del
      // propio control, así que aquí el rótulo va en la fila de arriba y el
      // control ocupa las dos filas: con un rótulo encima del suyo, la columna
      // se leía descuadrada respecto a las demás.
      return (
        <Fragment key={spec.key}>
          <label className={`${filterLabelCls(dark)} self-end`}>{t(spec.labelKey)}</label>
          <DateRangePicker
            dark={dark}
            desde={desde}
            hasta={hasta}
            onDesde={v => onChange(`${spec.key}Desde`, v)}
            onHasta={v => onChange(`${spec.key}Hasta`, v)}
          />
        </Fragment>
      )
    }

    const value = filters[spec.key] ?? ''
    const cls = filterFieldCls(dark, value !== '')

    if (spec.kind === 'select') {
      const opciones = distinctValues(rows, spec.field as keyof AnyReportRow & string)
      return (
        <Fragment key={spec.key}>
          {label}
          <select className={cls} value={value} onChange={e => onChange(spec.key, e.target.value)}>
            <option value="">{t('reports.filters.all')}</option>
            {/* El rótulo puede ir en inglés mientras el VALOR sigue siendo el de
                la vista (en español): es el que viaja al predicado. */}
            {opciones.map(o => (
              <option key={o} value={o}>{opcion(spec, o)}</option>
            ))}
          </select>
        </Fragment>
      )
    }

    // Multi-valor. Los dos comparten control; lo que cambia es de dónde salen
    // las opciones y qué significa elegirlas:
    //   · `multiSelect` — catálogo cerrado (proveedor, sección). Sólo lo que
    //     existe en el dataset, y casa por igualdad.
    //   · `multiText`  — identificadores (pedido Sterling, factura, artículo).
    //     El catálogo es una AYUDA, no un límite: sigue admitiendo texto libre
    //     porque quien pega media referencia espera que la encuentre igual.
    if (spec.kind === 'multiSelect' || spec.kind === 'multiText') {
      const catalogo = spec.kind === 'multiSelect'
        ? distinctValues(rows, spec.field as keyof AnyReportRow & string)
            .map(o => ({ valor: o, titulo: opcion(spec, o) }))
        // Con el nombre al lado del código: `MultiFiltro` lo pinta y además
        // busca por él, que es como se busca un artículo cuando no te sabes la
        // referencia de memoria.
        //
        // El título va FORMATEADO por rol en los filtros de artículo, igual que
        // la tabla: `ReportTable` recorta la letra de tipo desde que los reports
        // se abrieron al rol avp, y hasta ahora el desplegable seguía enseñando
        // el código crudo — el usuario elegía `XARF32120414` y en la tabla leía
        // `ARF32120414`. El VALOR sigue siendo el código crudo, que es el que
        // viaja al predicado y a la URL, y `MultiFiltro` busca también por él,
        // así que pegar la referencia entera sigue encontrando su opción.
        //
        // Dos artículos que sólo se diferencian en la letra de tipo salen ahora
        // con el mismo título; los separa su descripción, que va al lado, y en la
        // tabla la columna ITEM TYPE. Lo que ya no puede pasar es que elegir uno
        // traiga los dos: eso lo arregla `catalogosMultiText`, que hace que un
        // código completo case por igualdad y no por subcadena.
        : catalogoDe(spec)
      return (
        <Fragment key={spec.key}>
          {label}
          <MultiFiltro
            dark={dark}
            // Ancho fijo, no `min-w`: la rejilla dimensiona cada columna por su
            // contenido máximo, y el ancho intrínseco de un <input> suelto
            // (~218 px) desbordaba la fila de siete controles.
            className="w-[180px]"
            opciones={catalogo}
            titulosMono={spec.kind === 'multiText'}
            permitirLibre={spec.kind === 'multiText'}
            seleccion={valoresMulti(value)}
            onSeleccion={vs => onChange(spec.key, estadoMulti(vs))}
            placeholder={t('reports.filters.searchPlaceholder')}
          />
        </Fragment>
      )
    }

    if (spec.kind === 'bool') {
      return (
        <Fragment key={spec.key}>
          {label}
          <select className={cls} value={value} onChange={e => onChange(spec.key, e.target.value)}>
            <option value="">{t('reports.filters.all')}</option>
            <option value="1">{t('reports.filters.yes')}</option>
            <option value="0">{t('reports.filters.no')}</option>
          </select>
        </Fragment>
      )
    }

    return (
      <Fragment key={spec.key}>
        {label}
        <input
          type="search"
          className={cls}
          value={value}
          placeholder={t('reports.filters.searchPlaceholder')}
          onChange={e => onChange(spec.key, e.target.value)}
        />
      </Fragment>
    )
  }

  // Un chip por VALOR, no por filtro: es lo que permite quitar «este artículo»
  // sin perder los otros dos. Los rangos de fecha van como un solo chip porque
  // sus dos extremos son una sola condición.
  const filtrosActivos: FiltroActivo[] = def.filters.flatMap<FiltroActivo>(spec => {
    const campo = t(spec.labelKey)

    if (spec.kind === 'dateWindow') {
      const desde = filters[`${spec.key}Desde`] ?? ''
      const hasta = filters[`${spec.key}Hasta`] ?? ''
      if (!desde && !hasta) return []
      return [{
        clave: spec.key, campo,
        valor: [desde || '…', hasta || '…'].join(' – '),
        quitar: () => { onChange(`${spec.key}Desde`, ''); onChange(`${spec.key}Hasta`, '') },
      }]
    }

    const valor = filters[spec.key] ?? ''
    if (!valor) return []

    if (spec.kind === 'multiSelect' || spec.kind === 'multiText') {
      const elegidos = valoresMulti(valor)
      // El nombre no entra en el chip —lo alargaría hasta comerse la franja—,
      // sino en el tooltip, que es donde se consulta.
      const catalogo = spec.descripcionField || spec.esArticulo ? catalogoDe(spec) : []
      const nombreDe = spec.descripcionField ? descripcionDe(catalogo) : undefined
      const etiquetaDe = spec.esArticulo ? etiquetaCatalogoDe(catalogo) : undefined
      return elegidos.map(v => ({
        clave: `${spec.key}:${v}`, campo,
        valor: spec.kind === 'multiSelect' ? opcion(spec, v) : (etiquetaDe?.(v) ?? v),
        tooltip: nombreDe?.(v),
        quitar: () => onChange(spec.key, estadoMulti(elegidos.filter(x => x !== v))),
      }))
    }

    if (spec.kind === 'bool') {
      return [{
        clave: spec.key, campo,
        valor: t(valor === '1' ? 'reports.filters.yes' : 'reports.filters.no'),
        quitar: () => onChange(spec.key, ''),
      }]
    }

    return [{
      clave: spec.key, campo,
      valor: spec.kind === 'select' ? opcion(spec, valor) : valor,
      quitar: () => onChange(spec.key, ''),
    }]
  })

  return (
    <div className={filterCardCls(dark)}>
      {/* `filterCardCls` no trae padding a propósito: lo pone cada consumidor.
          Sin él los campos quedaban pegados al borde de la tarjeta.

          Rejilla y no flex: con flex y `items-end`, la columna del rango de
          fechas es más alta que las demás y arrastraba su etiqueta a una línea
          más arriba, dejando la fila de rótulos descuadrada. Con dos filas de
          rejilla (`grid-rows-[auto_auto]`) todas las etiquetas comparten línea
          base y todos los campos comparten la suya, sea cual sea el control. */}
      <div className="p-4 grid grid-flow-col auto-cols-max grid-rows-[auto_auto] items-end gap-x-4 gap-y-1.5 overflow-x-auto">
        {def.filters.map(campo)}

        {/* Niveles de los TOTALES, no de las filas: la tabla sigue mostrando la
            jerarquía completa. El rótulo dice «Totales de» y no «Nivel»
            justamente para no prometer un filtrado de filas que este control no
            hace.

            Admite VARIOS, cada uno con su propia fila de pie. Era de opción
            única con el argumento de que la cabecera ya es la suma de su detalle
            y totalizar los dos a la vez duplicaría — cierto sumándolos en la
            misma celda, y falso en filas separadas. Con opción única, ver los
            litros de los lotes y los kilos de los componentes obligaba a cambiar
            el selector, mirar, y volver a cambiarlo.

            Mismo control que los filtros multi-valor, y no un `select` con una
            opción «Todos»: así seleccionar dos de tres niveles se hace igual que
            seleccionar dos proveedores, y el atajo de todos/ninguno lo trae el
            propio control. */}
        <>
          <label className={`${filterLabelCls(dark)} self-end`}>{t('reports.filters.totalsOf')}</label>
          <MultiFiltro
            dark={dark}
            className="w-[180px]"
            opciones={def.levelOptions.map(o => ({ valor: o.tipoFila, titulo: t(o.labelKey) }))}
            seleccion={aggLevels}
            onSeleccion={onLevels}
            placeholder={t('reports.filters.levelPlaceholder')}
            // Los niveles son nombres, no códigos: en monoespaciada se afean.
            titulosMono={false}
            // Nunca está vacío: el resalte sólo si te has apartado del defecto.
            modificado={activeLevel !== null}
            title={t('reports.tooltips.totalsOf')}
          />
        </>

        {/* Siempre visible, aunque sin filtro de sublínea los dos modos den el
            mismo número: un control que aparece y desaparece se lee como una
            función nueva cada vez, y esta decide qué significa el pie. Aquí está
            el ajuste, y se ve en qué modo está antes de filtrar. */}
        <>
          <label className={`${filterLabelCls(dark)} self-end`}>{t('reports.filters.totalize')}</label>
          <select
            className={filterFieldCls(dark, !soloCoincidencias)}
            value={soloCoincidencias ? 'match' : 'block'}
            onChange={e => onSoloCoincidencias(e.target.value === 'match')}
            title={t('reports.tooltips.totalize')}
          >
            <option value="match">{t('reports.filters.totalizeMatches')}</option>
            <option value="block">{t('reports.filters.totalizeBlock')}</option>
          </select>
        </>

      </div>

      {/* Franja común, fuera de la rejilla de controles: añadir o quitar filtros
          no mueve ni un campo de sitio. */}
      {filtrosActivos.length > 0 && (
        <div className="px-4 pb-4 -mt-1">
          <BarraFiltrosActivos
            dark={dark}
            filtros={filtrosActivos}
            onLimpiar={onClear}
            resumen={resumen}
          />
        </div>
      )}
    </div>
  )
}
